import { SEO } from "@/components/SEO";
import { Helmet } from "react-helmet-async";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Link } from "react-router-dom";
import { 
  DollarSign, 
  Check, 
  X, 
  Smartphone, 
  Search, 
  Image, 
  FileText, 
  Settings,
  Calendar,
  ArrowRight,
  Clock,
  RefreshCw
} from "lucide-react";

const faqItems = [
  {
    question: "How much does a landscaping website cost per month?",
    answer: "Monthly costs vary by option: DIY builders run $15–$50/month, freelancer maintenance is $50–$200/month, and subscription services like ours cost $150–$350/month for a fully managed site with hosting, updates, SEO, and project galleries included."
  },
  {
    question: "Why do landscaping websites cost more than other small business sites?",
    answer: "Landscaping sites need high-quality project galleries, service pages for each offering (patios, retaining walls, planting, etc.), service area pages for local SEO, before/after comparisons, and quote request flows. These features require expertise to build correctly."
  },
  {
    question: "Is it cheaper to build my own landscaping website?",
    answer: "DIY builders have the lowest upfront cost ($0–$500), but you'll spend 30–50+ hours learning and building—especially for gallery-heavy landscaping sites. If your time is worth $75/hour, that's $2,250–$3,750 in time alone."
  },
  {
    question: "What's included in a landscaping website subscription?",
    answer: "Our subscription includes custom design, hosting, SSL, unlimited edits, project galleries, SEO-ready structure, mobile optimization, service pages, service area pages, quote forms, and ongoing support—no hidden fees."
  },
  {
    question: "Do I need a portfolio gallery on my landscaping website?",
    answer: "Absolutely. Landscaping is visual—homeowners want to see your work before calling. A well-organized gallery with before/after shots, project descriptions, and location tags can be the difference between a quote request and a bounce."
  },
  {
    question: "How much should I budget for a professional landscaping website?",
    answer: "Budget $3,000–$8,000 for a quality one-time build with galleries, or $150–$350/month for a subscription. The subscription often makes more sense because it includes hosting, updates, gallery management, and support."
  },
  {
    question: "Are cheap landscaping websites worth it?",
    answer: "Sub-$1,000 websites typically use generic templates, lack proper galleries, miss local SEO, and don't convert high-ticket landscaping leads. You'll likely rebuild within 1–2 years. Investing in quality upfront saves money long-term."
  },
  {
    question: "What hidden costs come with landscaping websites?",
    answer: "Watch for: annual hosting ($100–$300), domain renewal ($15–$20), SSL certificates ($0–$100), gallery plugins/storage, security monitoring, and revision fees. Subscriptions typically bundle all of these into one payment."
  },
  {
    question: "How long does it take to get a return on my website investment?",
    answer: "Most landscaping companies see ROI within 2–4 months. One patio or retaining wall project ($5,000–$15,000+) pays for years of website costs. Even a single hardscaping lead per month justifies the investment."
  },
  {
    question: "Can I start with a basic site and add galleries later?",
    answer: "Yes, but retrofitting galleries is harder than building them in from the start. If budget is tight, start with a subscription—you get professional galleries immediately with predictable monthly costs."
  }
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": "https://www.leafrushmarketing.com/landscaping-website-cost/#faq",
  "mainEntity": faqItems.map(item => ({
    "@type": "Question",
    "name": item.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": item.answer
    }
  }))
};

const webPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": "https://www.leafrushmarketing.com/landscaping-website-cost/#webpage",
  "url": "https://www.leafrushmarketing.com/landscaping-website-cost/",
  "name": "Landscaping Website Cost: 2024 Pricing Guide | Leaf Rush Marketing",
  "description": "How much does a landscaping website cost? Compare DIY, freelancer, agency, and subscription pricing. Real numbers for landscapers and hardscapers.",
  "isPartOf": {
    "@id": "https://www.leafrushmarketing.com/#website"
  },
  "about": {
    "@id": "https://www.leafrushmarketing.com/#organization"
  },
  "dateModified": new Date().toISOString().split('T')[0]
};

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": "https://www.leafrushmarketing.com/#organization",
  "name": "Leaf Rush Marketing",
  "url": "https://www.leafrushmarketing.com",
  "logo": "https://www.leafrushmarketing.com/og-image.jpg",
  "sameAs": []
};

const LandscapingWebsiteCost = () => {
  const today = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <>
      <SEO 
        title="Landscaping Website Cost: 2024 Pricing Guide"
        description="How much does a landscaping website cost? Compare DIY, freelancer, agency, and subscription pricing. Real numbers for landscapers and hardscapers."
        canonical="https://www.leafrushmarketing.com/landscaping-website-cost/"
        ogUrl="https://www.leafrushmarketing.com/landscaping-website-cost/"
        ogImage="https://www.leafrushmarketing.com/og-image.jpg"
        keywords="landscaping website cost, landscaping website pricing, landscaper website cost, landscaping website monthly cost, landscaping website subscription, how much does a landscaping website cost"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      </Helmet>
      <div className="pt-24">
        {/* Hero */}
        <section className="py-12 md:py-16 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <Badge className="mb-4">Pricing Guide</Badge>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
              How much does a landscaping website cost?
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl">
              Real pricing for landscapers and hardscapers—from patios to retaining walls. No fluff, just numbers to help you make a smart decision.
            </p>
            <p className="text-sm text-muted-foreground mt-4">Last updated: {today}</p>
          </div>
        </section>

        {/* Quick Answer Box */}
        <section className="py-12 bg-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <Card className="border-2 border-primary/20">
              <CardContent className="p-6 md:p-8">
                <div className="flex items-center gap-2 mb-6">
                  <DollarSign className="h-6 w-6 text-primary" />
                  <h2 className="text-2xl font-bold">Typical Cost Ranges</h2>
                </div>
                
                <div className="grid gap-4">
                  {/* DIY Builder */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div>
                      <h3 className="font-semibold">DIY Builder</h3>
                      <p className="text-sm text-muted-foreground">Wix, Squarespace, WordPress.com</p>
                    </div>
                    <div className="mt-2 sm:mt-0 text-right">
                      <p className="font-bold text-lg">$0–$500</p>
                      <p className="text-sm text-muted-foreground">+ $20–$50/mo hosting</p>
                    </div>
                  </div>

                  {/* Freelancer */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div>
                      <h3 className="font-semibold">Freelancer</h3>
                      <p className="text-sm text-muted-foreground">One-time build, quality varies</p>
                    </div>
                    <div className="mt-2 sm:mt-0 text-right">
                      <p className="font-bold text-lg">$1,000–$5,000</p>
                      <p className="text-sm text-muted-foreground">+ hosting & maintenance</p>
                    </div>
                  </div>

                  {/* Agency */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div>
                      <h3 className="font-semibold">Agency</h3>
                      <p className="text-sm text-muted-foreground">Custom design, full service</p>
                    </div>
                    <div className="mt-2 sm:mt-0 text-right">
                      <p className="font-bold text-lg">$5,000–$15,000+</p>
                      <p className="text-sm text-muted-foreground">+ $150–$400/mo ongoing</p>
                    </div>
                  </div>

                  {/* Subscription */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-primary/10 rounded-lg border border-primary/20">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">Subscription (Leaf Rush)</h3>
                        <Badge variant="secondary" className="text-xs">Recommended</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">Everything included, cancel anytime</p>
                    </div>
                    <div className="mt-2 sm:mt-0 text-right">
                      <p className="font-bold text-lg text-primary">$150–$350/mo</p>
                      <p className="text-sm text-muted-foreground">No upfront cost</p>
                    </div>
                  </div>
                </div>

                <p className="text-sm text-muted-foreground mt-6">
                  See our <Link to="/websites" className="text-primary hover:underline">website subscription pricing</Link> for exact numbers.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* What Changes the Price */}
        <section className="py-12 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold mb-6">What changes the price?</h2>
            <p className="text-muted-foreground mb-8">
              Landscaping websites need more than basic sites. Here's what drives costs:
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
              <Card>
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <FileText className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <h3 className="font-semibold">Number of Services</h3>
                      <p className="text-sm text-muted-foreground">
                        Patios, retaining walls, planting, lighting = more <Link to="/service-pages-that-rank/" className="text-primary hover:underline">service pages</Link>
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <Search className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <h3 className="font-semibold">Service Areas</h3>
                      <p className="text-sm text-muted-foreground">
                        Each city needs a <Link to="/service-area-pages-local-seo/" className="text-primary hover:underline">service area page</Link> to rank locally
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <Image className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <h3 className="font-semibold">Project Gallery</h3>
                      <p className="text-sm text-muted-foreground">
                        Before/after shots, categorized projects, drone photos add complexity
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <Settings className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <h3 className="font-semibold">Booking & Quote Routing</h3>
                      <p className="text-sm text-muted-foreground">
                        Quote forms, project type routing, calendar integration
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <FileText className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <h3 className="font-semibold">Copywriting</h3>
                      <p className="text-sm text-muted-foreground">
                        Professional copy that sells high-ticket services = better conversions
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-5">
                  <div className="flex items-start gap-3">
                    <Search className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <h3 className="font-semibold">SEO & Reviews</h3>
                      <p className="text-sm text-muted-foreground">
                        Schema markup, <Link to="/growth-add-ons/gbp" className="text-primary hover:underline">GBP optimization</Link>, review integration
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* What a Good Landscaping Website Includes */}
        <section className="py-12 bg-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold mb-6">What a "good" landscaping website includes</h2>
            <p className="text-muted-foreground mb-8">
              Before comparing prices, know what you're paying for. A <Link to="/landscaping-website-design/" className="text-primary hover:underline">landscaping website design</Link> that converts high-ticket leads should have:
            </p>

            <div className="grid sm:grid-cols-2 gap-3">
              {[
                "Gallery-heavy design (your work sells itself)",
                "Before/after project comparisons",
                "Service pages for each offering",
                "Service area pages for local SEO",
                "Mobile-first (60%+ visitors on phones)",
                "Fast loading (under 3 seconds)",
                "Quote request form with project type",
                "Customer reviews/testimonials",
                "Trust signals (license, insurance, awards)",
                "SSL certificate (https://)",
                "Google Business Profile integration",
                "Schema markup for local search"
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                  <span className="text-sm">{item}</span>
                </div>
              ))}
            </div>

            <p className="text-sm text-muted-foreground mt-6">
              Need help? Check our <Link to="/resources" className="text-primary hover:underline">resources</Link> for website checklists and guides.
            </p>
          </div>
        </section>

        {/* Subscription Model Explained */}
        <section className="py-12 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold mb-6">Why we use a subscription model</h2>
            
            <div className="space-y-6">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <DollarSign className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">No big upfront cost</h3>
                      <p className="text-muted-foreground">
                        Skip the $5,000–$15,000 lump sum. Start with a predictable monthly payment that fits your cash flow—especially helpful during slow seasons.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <RefreshCw className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Unlimited updates included</h3>
                      <p className="text-muted-foreground">
                        Add new projects to your gallery, update service pages, add new service areas—all included. No $50–$150/hour revision fees.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Clock className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Cancel anytime</h3>
                      <p className="text-muted-foreground">
                        No long-term contracts. If it's not working, cancel. We'll export your content so you own everything.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Settings className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">Everything bundled</h3>
                      <p className="text-muted-foreground">
                        Hosting, SSL, security updates, backups, gallery management, SEO—one monthly payment covers it all.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Who It's Best For */}
            <div className="mt-8">
              <h3 className="text-xl font-semibold mb-4">Who subscription is best for:</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  "Landscapers focused on projects, not websites",
                  "Companies adding new work to portfolios regularly",
                  "Businesses expanding into new service areas",
                  "Teams without in-house web developers",
                  "Anyone who hates surprise invoices"
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <Check className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <span className="text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-12 bg-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold mb-6">Quick comparison</h2>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-2 font-semibold">Feature</th>
                    <th className="text-center py-3 px-2 font-semibold">DIY</th>
                    <th className="text-center py-3 px-2 font-semibold">Freelancer</th>
                    <th className="text-center py-3 px-2 font-semibold">Agency</th>
                    <th className="text-center py-3 px-2 font-semibold bg-primary/5">Subscription</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { feature: "Upfront cost", diy: "$0–$500", freelancer: "$1K–$5K", agency: "$5K–$15K+", sub: "$0" },
                    { feature: "Monthly cost", diy: "$20–$50", freelancer: "$50–$200", agency: "$150–$400", sub: "$150–$350" },
                    { feature: "Project gallery", diy: "Basic", freelancer: "Varies", agency: "Custom", sub: "Custom" },
                    { feature: "Unlimited edits", diy: false, freelancer: false, agency: false, sub: true },
                    { feature: "SEO included", diy: false, freelancer: "Maybe", agency: true, sub: true },
                    { feature: "Hosting included", diy: true, freelancer: false, agency: "Maybe", sub: true },
                    { feature: "Support", diy: "DIY", freelancer: "Email", agency: "Good", sub: "Priority" },
                    { feature: "Time to launch", diy: "40+ hrs", freelancer: "2–4 wks", agency: "4–8 wks", sub: "2–3 wks" },
                  ].map((row, index) => (
                    <tr key={index} className="border-b">
                      <td className="py-3 px-2 font-medium">{row.feature}</td>
                      <td className="text-center py-3 px-2">
                        {typeof row.diy === 'boolean' ? (
                          row.diy ? <Check className="h-4 w-4 text-primary mx-auto" /> : <X className="h-4 w-4 text-muted-foreground mx-auto" />
                        ) : row.diy}
                      </td>
                      <td className="text-center py-3 px-2">
                        {typeof row.freelancer === 'boolean' ? (
                          row.freelancer ? <Check className="h-4 w-4 text-primary mx-auto" /> : <X className="h-4 w-4 text-muted-foreground mx-auto" />
                        ) : row.freelancer}
                      </td>
                      <td className="text-center py-3 px-2">
                        {typeof row.agency === 'boolean' ? (
                          row.agency ? <Check className="h-4 w-4 text-primary mx-auto" /> : <X className="h-4 w-4 text-muted-foreground mx-auto" />
                        ) : row.agency}
                      </td>
                      <td className="text-center py-3 px-2 bg-primary/5 font-medium">
                        {typeof row.sub === 'boolean' ? (
                          row.sub ? <Check className="h-4 w-4 text-primary mx-auto" /> : <X className="h-4 w-4 text-muted-foreground mx-auto" />
                        ) : row.sub}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-12 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-2xl md:text-3xl font-bold mb-6">Frequently asked questions</h2>
            
            <Accordion type="single" collapsible className="w-full">
              {faqItems.map((item, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 bg-primary/5">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Ready to get your landscaping website?</h2>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
              Book a free 15-minute call to discuss your business and get a custom quote. No pressure, no obligations.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" asChild>
                <a href="#get-started">
                  Get Started
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/websites">
                  See Website Subscription Pricing
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Related Links */}
        <section className="py-12 bg-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-xl font-semibold mb-4">Related reading</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link to="/landscaping-website-design/" className="p-4 border rounded-lg hover:border-primary/50 transition-colors">
                <h3 className="font-medium mb-1">Landscaping Website Design</h3>
                <p className="text-sm text-muted-foreground">What makes a landscaping website convert</p>
              </Link>
              <Link to="/lawn-care-website-cost/" className="p-4 border rounded-lg hover:border-primary/50 transition-colors">
                <h3 className="font-medium mb-1">Lawn Care Website Cost</h3>
                <p className="text-sm text-muted-foreground">Pricing guide for lawn care companies</p>
              </Link>
              <Link to="/service-pages-that-rank/" className="p-4 border rounded-lg hover:border-primary/50 transition-colors">
                <h3 className="font-medium mb-1">Service Pages That Rank</h3>
                <p className="text-sm text-muted-foreground">Structure guide for service pages</p>
              </Link>
              <Link to="/contact" className="p-4 border rounded-lg hover:border-primary/50 transition-colors">
                <h3 className="font-medium mb-1">Contact Us</h3>
                <p className="text-sm text-muted-foreground">Get in touch with questions</p>
              </Link>
            </div>
          </div>
        </section>

        {/* Calendly */}
        <section id="schedule-call">
          <div className="container mx-auto px-4 max-w-4xl">
            <CalendlyEmbed />
          </div>
        </section>
      </div>
    </>
  );
};

export default LandscapingWebsiteCost;
